import './MapContainerComponent.less';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  MapMarker,
  MapBounds,
  ClusterGroup,
  MapViewState,
  HoverCardData,
} from '../../types/map';
import { HotelData } from '../../modules/HotelDetails/hotelSlice';
import MapPin from './components/MapPin';
import {
  transformHotelsToMapMarkers,
  tileLayerSetOpenstreetmap,
  tileLayerSetCartocdn,
  tileLayerSetCartodb,
  tileLayerCartoVoyager,
  calculateRadiusFromBounds,
  MAP_MAX_NATIVE_ZOOM,
  MAP_MAX_ZOOM,
  tileLayerCartoPositronLight,
  MARKER_TYPE,
} from './util/MapHelpers';
import useGetGrossPrice from '../../hooks/useGetGrossPrice';
// import { IoLocationSharp } from 'react-icons/io5';
import { LeftOutlined } from '@ant-design/icons';
// Fix for default markers in react-leaflet
import 'leaflet/dist/leaflet.css';
import L, { LatLngExpression, map } from 'leaflet';
import {
  TileLayer,
  Popup,
  FeatureGroup,
  Marker,
  LayersControl,
  useMapEvent,
  ScaleControl,
  ZoomControl,
} from 'react-leaflet';
import { MapEventsHandler } from './hooks/useMapEventsHandler';
import MapCircle from './components/MapCircle';
import { ControllingGroup } from './components/ControllingGroup';
import { Button, message } from 'antd';
import Legend from './components/Legend';
import RecenterButton from './components/RecenterButton';
import 'react-leaflet-cluster/dist/assets/MarkerCluster.css';
import 'react-leaflet-cluster/dist/assets/MarkerCluster.Default.css';
import MarkerClusterGroup from 'react-leaflet-cluster';
import MapPlaceholder from './components/MapPlaceholder';
import { useHotelSearchByGeo } from '../../modules/SwitchDatePicker/components/SearchResults/SearchResultsHotels/hooks/useHotelSearchAPI';
import { useReactiveURLSearchParams } from '../../hooks/useReactiveURLSearchParams';
import SpinLoader from '@skygroup/shared/components/spin-loader/SpinLoader';
import { OptionalFilter } from '../SideFilters/interface';
import SideFilters from '../SideFilters/SideFilters';
import HotelSearchInput from '../../modules/SwitchDatePicker/components/SearchResults/SearchResultsHotels/components/HotelSearchInput';
import { useHotelSearch } from '../../modules/SwitchDatePicker/components/SearchResults/SearchResultsHotels/hooks/useHotelSearch';
import Sider from 'antd/lib/layout/Sider';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { useCalcTwoPrices } from '../../utils/convertEurToUsd';
import { currencySignsObj } from '@skygroup/shared/utils/constants';
import FetchMoreButton from './components/FetchMoreButton';
import SafeMapContainer from './SafeMapContainer';

interface Props {
  children?: React.ReactNode;
  // data: HotelData[] | dynamicPackageData[] | attractionData[];
  initialMarkersArr: MapMarker[];
  initPosition: { lat: number; lng: number };
  optionalFilters?: OptionalFilter[];
  isFullyLoaded: boolean;
  onHotelHover?: (
    item: MapMarker | null,
    position?: { x: number; y: number }
  ) => void;
  // onViewportChange?: (viewport: MapViewState) => void;
  // initPosition?: LatLngExpression;
  initialZoom?: number;
  renderHoverCard?: (data: HoverCardData) => React.ReactNode;
  showCircleRadius?: boolean;
  selectedMarkerId?: string;
  handleItemClick?: (id: string) => void;
  is_dp?: boolean;
  hasNameSearch?: boolean;
  searchHotelsByGeo?: boolean;
  handleSelectMarker?: (id: string) => void;
  localSideFilters?: Record<string, any> | null;
  setLocalSideFilters?: (f: Record<string, any>) => void;
  showMarkerNameLabel?: boolean;
}

const MapContainerComponent: React.FC<Props> = ({
  children,
  initialMarkersArr,
  optionalFilters,
  isFullyLoaded,
  onHotelHover,
  // onViewportChange,
  initPosition,
  initialZoom = 13,
  renderHoverCard,
  // showCircleRadius = false,
  selectedMarkerId,
  is_dp = false,
  hasNameSearch = false,
  searchHotelsByGeo = false,
  handleSelectMarker,
  localSideFilters,
  setLocalSideFilters,
  showMarkerNameLabel = false,
}) => {
  const {
    direction,
    config: { siteConfig },
    translations: { general: general_t },
  } = useSelector((data: RootState) => data.config);
  const mapRef = useRef<L.Map | null>(null);
  const [mapViewPort, setMapViewPort] = useState<{
    center: LatLngExpression;
    zoom: number;
    bounds: MapBounds | null;
  }>({
    center: initPosition || mapRef.current?.getCenter(),
    zoom: initialZoom,
    bounds: {
      north: mapRef?.current?.getBounds().getNorth() || 0,
      south: mapRef?.current?.getBounds().getSouth() || 0,
      east: mapRef?.current?.getBounds().getEast() || 0,
      west: mapRef?.current?.getBounds().getWest() || 0,
    },
  });

  const [animatePanning, setAnimatePanning] = useState(true);
  const mapSidebarRef = useRef<HTMLDivElement>(null);
  const [mapReady, setMapReady] = useState(false);

  const getGrossPrice = useGetGrossPrice();

  const urlSearchParams = useReactiveURLSearchParams();

  const radius = useMemo(() => {
    return mapViewPort?.bounds
      ? calculateRadiusFromBounds(mapViewPort?.bounds)
      : undefined;
  }, [mapViewPort?.bounds]);

  const {
    data: hotelsByGeoData,
    refetch: refetchByGeo,
    isFetching: isFetchingByGeo,
    isLoading: isLoadingByGeo,
    isSuccess: isSuccessByGeo,
  } = useHotelSearchByGeo({
    searchParams: urlSearchParams,
    latitude: Array.isArray(mapViewPort?.center)
      ? mapViewPort?.center[0]
      : mapViewPort?.center.lat,
    longitude: Array.isArray(mapViewPort?.center)
      ? mapViewPort?.center[1]
      : mapViewPort?.center.lng,
    radius,
    is_dp: is_dp ? 1 : 0,
  });

  const {
    hotelNameKeyWord,
    debouncedSearchKeyWord,
    handleHotelNameInputOnChange,
  } = useHotelSearch({ disabled: !searchHotelsByGeo });

  const handleRefetchByGeo = useCallback(() => {
    if (debouncedSearchKeyWord || !searchHotelsByGeo) {
      return;
    }
    if (radius && (radius < 1 || radius > 50 * 1000)) {
      // message.error('Radius must be between 1 and 50000');
      return;
    }
    refetchByGeo();
  }, [refetchByGeo, debouncedSearchKeyWord, radius, searchHotelsByGeo]);

  const markersArr = useMemo(() => {
    const markers = !!hotelsByGeoData?.length
      ? transformHotelsToMapMarkers(hotelsByGeoData)
      : initialMarkersArr;

    if (debouncedSearchKeyWord) {
      return markers.filter((marker) =>
        marker.info.name
          .toLowerCase()
          .includes(debouncedSearchKeyWord.toLowerCase())
      );
    }

    return markers;
  }, [hotelsByGeoData, initialMarkersArr, debouncedSearchKeyWord]);

  useEffect(() => {
    if (isFullyLoaded && mapReady) {
      message.success('Map loaded');
    }
  }, [isFullyLoaded, mapReady]);


  // Fit map to bounds when data change
  // useEffect(() => {
  //   const initialBounds = calculateInitialBounds(markersArr);
  //   if (mapRef.current && initialBounds && markersArr.length > 1) {
  //     mapRef.current.fitBounds(initialBounds, {
  //       padding: [20, 20],
  //     });
  //   }
  // }, [markersArr]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      mapRef.current?.invalidateSize();
    }, 300);
    return () => {
      clearTimeout(timeout);
    };
  }, []);

  const { calcTwoPrices } = useCalcTwoPrices();
  const [sidebarListVisible, setSidebarListVisible] = useState(true);
  const handleListVisibility = useCallback(() => {
    setSidebarListVisible((prev) => !prev);
  }, []);


  const renderMarkerItems = useCallback(() => {
    return (
      <MarkerClusterGroup
        chunkedLoading
        // zoomToBoundsOnClick={true}
        // spiderfyDistanceMultiplier={2}
        // disableClusteringAtZoom
        // maxClusterRadius={80}
        // spiderfyOnMaxZoom={true}
        // zoomToBoundsOnClick={false}
        // showCoverageOnHover
        disableClusteringAtZoom={17} // ensures markers stay expanded at high zoom
        spiderfy
      >
        {markersArr.map((item, index) => {
          const unitItem = item as MapMarker;
          if (unitItem?.price === undefined || unitItem?.currency === undefined) {
            return null;
          }

          let price = getGrossPrice(
            unitItem?.price,
            unitItem?.agent_additional_info?.suggested_commission
          );
          let currency = unitItem?.currency;

          if (item?.flightPriceInfo) {
            const sumOfGrossPrices = calcTwoPrices({
              price1: getGrossPrice(
                item?.flightPriceInfo?.price,
                siteConfig?.shareB2C?.dp
              ),
              curr1: item?.flightPriceInfo?.currency,
              price2: getGrossPrice(unitItem?.price, siteConfig?.shareB2C?.dp),
              curr2: unitItem?.currency,
            });
            const sumOfNetPrices = calcTwoPrices({
              price1: item?.flightPriceInfo?.price,
              curr1: item?.flightPriceInfo?.currency,
              price2: unitItem?.price,
              curr2: unitItem?.currency,
            });
            const minPrice = Number(
              (sumOfGrossPrices / item?.flightPriceInfo?.totalPax)?.toFixed(0)
            );
            const net_min_price =
              sumOfNetPrices / item?.flightPriceInfo?.totalPax;
            price = minPrice;
            currency = currencySignsObj['usd'];
          }

          let priceLabel = price && currency ? `${price} ${currency}` : undefined;
          if (showMarkerNameLabel) {
            priceLabel = `${item.name}`;
          }
          return (
            <MapPin
              key={unitItem.identifier}
              map={mapRef.current}
              item={unitItem}
              renderHoverCard={renderHoverCard}
              priceLabel={priceLabel}
              renderTooltip={mapViewPort?.zoom > 10}
              // center={{ lat: item.lat, lng: item.lng }}
              handleSelectMarker={handleSelectMarker}
              openPopup={selectedMarkerId === unitItem.identifier}
              dir={direction}
            />
          );
        })}
      </MarkerClusterGroup>
    );
  }, [markersArr]);

  return (
    <>
      {!!optionalFilters?.length && (
        <div
          className={`map-sidebar ${!sidebarListVisible ? 'collapsed' : ''}`}
          ref={mapSidebarRef}
        >
          {optionalFilters && (
            <div className={'map-sidebar-sidefilters'}>
              <SideFilters optional_filters={optionalFilters} isCompact            localSideFilters={localSideFilters}
          setLocalSideFilters={setLocalSideFilters}/>
            </div>
          )}

          <div
            className={`content ${hasNameSearch ? 'hasNameSearch' : ''} ${
              sidebarListVisible ? 'isVisible' : ''
            }`}
          >
            {hasNameSearch && (
              <div>
                <div>{`${'Showing'} ${markersArr.length} ${'results'}`}</div>
                <div>
                  <HotelSearchInput
                    value={hotelNameKeyWord}
                    onChange={handleHotelNameInputOnChange}
                    isCompact
                    includeAutocompleteAPI
                  />
                </div>
              </div>
            )}
            <div className={'list'} id="map-sidebar-list">
              <SpinLoader isLoading={isFetchingByGeo} onTop={true}>
                {children}
              </SpinLoader>
            </div>
          </div>

          <Button
            onClick={handleListVisibility}
            className={`collapse-list-btn ${
              sidebarListVisible ? 'isVisible' : ''
            }`}
          >
            <LeftOutlined size={24} color="#000" />
          </Button>
        </div>
      )}

      {searchHotelsByGeo && <FetchMoreButton
        map={mapRef.current}
        buttonText={general_t.refresh}
        tooltipText={`${general_t.refresh} ${general_t.list}`}
        refetchByGeo={handleRefetchByGeo}
      />}

      <SafeMapContainer
        ref={mapRef}
        whenReady={() => setMapReady(true)}
        center={initPosition}
        zoom={initialZoom}
        maxZoom={MAP_MAX_ZOOM}
        placeholder={<MapPlaceholder />}
        zoomControl={false}
      >
        <ScaleControl imperial={false} position={direction === 'ltr' ? 'bottomright' : 'bottomleft'} />
        <ZoomControl
          position={direction === 'ltr' ? 'bottomright' : 'bottomleft'}
        />
        <TileLayer
          {...tileLayerCartoVoyager}
          maxNativeZoom={MAP_MAX_NATIVE_ZOOM} // server limit, necessary to use with maxZoom
          maxZoom={MAP_MAX_ZOOM} // client limit, necessary to use with maxNativeZoom
        />

        <MapEventsHandler
          setMapViewPort={setMapViewPort}
          animatePanning={animatePanning}
          // refetchByGeo={handleRefetchByGeo}
          handleSelectMarker={handleSelectMarker}
        />

        {/* {renderVisibleItems()} */}
        {/* <LayersControl position="topright" collapsed={false}>
          {renderLayersControlOverlay()}
        </LayersControl> */}
        {!isFetchingByGeo && renderMarkerItems()}

        {/* <RecenterButton
          map={mapRef.current}
          center={[initPosition?.lat, initPosition?.lng]}
        /> */}
        {/* <FetchMoreButton
          map={mapRef.current}
          refetchByGeo={handleRefetchByGeo}
        /> */}
        {/* <Legend
          map={mapRef.current}
          colors={colors}
          labels={labels}
          center={[initPosition?.lat, initPosition?.lng]}
        /> */}
      </SafeMapContainer>
    </>
  );
};

export default MapContainerComponent;
